import { useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import theme from 'themes/default';
import GET_UI from '../apollo-cache-queries/getUiQuery';
import { WithTypename, StoreTypename, ViewStyle } from '../types';
import { useModal, UseModalDispatch, UseModalState } from 'providers/ModalProvider';
import { UseLoadingDispatch, useLoading, UseLoadingState } from 'providers/LoadingProvider';
import { UseAlertDispatch, useAlert } from 'providers/AlertProvider';

export type UiStoreState = {
  id: string;
  isDesktop: boolean;
  viewStyle: ViewStyle;
  navigationDrawerOpen: boolean;
} & UseLoadingState & { modal: UseModalState } & WithTypename<StoreTypename.STORE_UI>;

export type UiStoreDispatch = {
  setIsDesktop: (isDesktop: boolean) => void;
  setViewStyle: (viewStyle: ViewStyle) => void;
  setNavigationDrawer: (isOpen: boolean) => void;
} & UseAlertDispatch &
  UseModalDispatch &
  UseLoadingDispatch;

export type UiStore = [UiStoreState, UiStoreDispatch];

/**
 * Omit from ApolloStore all state kept in Providers (which render JSX)
 */
export type UiApolloStoreState = Omit<UiStoreState, 'modal' | 'isLoading'>;

export const getDefaultUiState = (): UiApolloStoreState => ({
  id: StoreTypename.STORE_UI,
  isDesktop: true,
  navigationDrawerOpen: true,
  viewStyle: ViewStyle.TILE,
  __typename: StoreTypename.STORE_UI
});

const useUiStore = (): UiStore => {
  const [modalState, modalDispatch] = useModal();
  const [, alertDispatch] = useAlert();
  const [loadingState, loadingDispatch] = useLoading();
  const client = useApolloClient();
  const { data } = useQuery(GET_UI);
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const setIsDesktop = (isDesktop: boolean): void => {
    client.writeQuery({
      query: GET_UI,
      data: {
        ui: { ...data?.ui, isDesktop }
      }
    });
  };
  const setViewStyle = (viewStyle: ViewStyle): void => {
    client.writeQuery({
      query: GET_UI,
      data: {
        ui: { ...data?.ui, viewStyle }
      }
    });
  };

  const setNavigationDrawer = useCallback(
    (isOpen: boolean): void => {
      client.writeQuery({
        query: GET_UI,
        data: {
          ui: {
            id: StoreTypename.STORE_UI,
            __typename: StoreTypename.STORE_UI,
            navigationDrawerOpen: isOpen
          }
        }
      });
    },
    [client]
  );

  return [
    {
      ...(data?.ui ?? getDefaultUiState()),
      isDesktop,
      modal: modalState,
      ...loadingState
    },
    {
      setIsDesktop,
      setViewStyle,
      setNavigationDrawer,
      ...modalDispatch,
      ...loadingDispatch,
      ...alertDispatch
    }
  ];
};

export default useUiStore;
